import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from "@mui/icons-material/Done";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import PeopleIcon from "@mui/icons-material/People";
import { Button, Divider, MenuItem, Select, Stack, Tooltip } from "@mui/material";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { assetService, cacheService, ingestService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { BaseMetadataType } from "../../../Core/Api/Types/base-metadata-type";
import { CreateNewDbMetadataValue } from "../../../Core/Utils/asset-object-utils";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { UserHasAcl, UserHasAcls } from "../../../Utils/helpers";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import FormInputNumber from "../../Forms/FormInputNumber/form-input-number";
import { ModalContext } from "../../Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Modals/ModalConfirmAction/modal-confirm-action";
import ModalSetFollowers, { ModalSetFollowersElementProps } from "../../Modals/ModalSetFollowers/modal-set-followers";
import SelectModeBanner from "../SelectModeBanner";

export interface IngestBannerProps {
  handleCloseClick: React.MouseEventHandler;
  items: number[];
  enableIntMetadataTagging?: boolean;
  enableDelete?: boolean;
}

const IngestBanner: React.FC<IngestBannerProps> = (props) => {
  const { t } = useTranslation();
  const { items, enableIntMetadataTagging, enableDelete, handleCloseClick } = props;
  const [openTooltipIntIngestStatus, setOpenTooltipIntIngestStatus] = useState(false);
  const [openTooltipIntBillingStatus, setOpenTooltipIntBillingStatus] = useState(false);
  const [openTooltipIntWoIdStatus, setOpenTooltipIntWoIdStatus] = useState(false);
  const customSnackbar = useCustomSnackbar();
  const [value, setValue] = useState("");
  const metadata = cacheService.GetMetadata();

  // Modals
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const handleSetFollowers = () => {
    openModal &&
      openModal<ModalSetFollowersElementProps>(ModalSetFollowers, {
        objectIds: items,
        objectType: "asset",
        currentFollowersIds: [],
      });
  };

  const handleDelete = () => {
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: true,
      commentLabel: t("modal:comment.confirmDeleteRequests"),
      title: t("modal:title.deleteRequests"),
      onConfirm: (comment) => {
        items.forEach((item) => {
          ingestService
            .DeleteRequest({ id: item, comment: comment ?? "User has deleted request from UI" })
            .then((response) => {
              if (response.success) {
                if (response.objectId === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID)
                  customSnackbar(response.message!, "primary");
                else customSnackbar(t("alert:success.requestDeleted", { item }), "success");
              } else
                customSnackbar(t("alert:warning.errorDeletingRequest", { item, message: response.message }), "warning");
            });
        });
      },
    });
  };

  const handleUpdate = useCallback(
    (type: "ingestStatus" | "billingStatus" | "btId") => {
      let field = metadata.find((x) => x.tagName === type);
      if (!field) return;

      items.forEach((id) => {
        assetService
          .UpdateMetadataFields({
            objectId: id,
            metadata: [CreateNewDbMetadataValue(field!.id, BaseMetadataType.DbStringMetadataValueInfos, type, value)],
          })
          .then((response) => {
            if (response.success) customSnackbar(t("alert:success.metadataUpdatedSuccess", { id }), "success");
            else customSnackbar(t("alert:warning.errorUpdatingMetadata", { id, message: response.message }), "warning");
          });
      });

      setOpenTooltipIntIngestStatus(false);
      setOpenTooltipIntBillingStatus(false);
      setOpenTooltipIntWoIdStatus(false);
    },
    [value, metadata, items]
  );

  const _renderTooltip = (type: "ingestStatus" | "billingStatus" | "btId") => {
    // Form must be unique to validate correctly input data
    const formId = `request-int-meta-form-${type}`;

    return (
      <Stack sx={{ width: 150 }} pt={0.5} pb={0.5} spacing={1}>
        <Button variant="contained" startIcon={<DoneIcon />} onClick={() => handleUpdate(type)}>
          {t("common:submit")}
        </Button>

        {type === "btId" && (
          <FormInputNumber
            label={t("field:label.enterWorkOrderId")}
            value={value}
            onChange={(e) => {
              setValue(e.currentTarget.value);
            }}
            validationProps={{
              formId: formId,
              formSectionId: "section",
              fieldId: "value",
              required: true,
              minValue: 1,
              maxValue: 900000,
              initialState: true,
              notDecimal: true,
              positive: true,
            }}
            stackProps={{}}
          />
        )}
        {(type === "billingStatus" || type === "ingestStatus") && (
          <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600, zIndex: 2001 } } }}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            sx={{ zIndex: 2001 }}
          >
            <MenuItem value="" selected sx={{ zIndex: 2001 }}>
              {type === "billingStatus" ? t("common:billing") : t("common:ingestStatus")}
            </MenuItem>
            {metadata
              .find((x) => x.tagName === type)
              ?.values?.flatMap((x) => x.value)
              .map((x) => (
                <MenuItem key={x!} value={x!} sx={{ zIndex: 2001 }}>
                  {x}
                </MenuItem>
              ))}
          </Select>
        )}
      </Stack>
    );
  };

  return (
    <SelectModeBanner text={t("banner:body.itemSelected", { count: items.length })} onCloseClick={handleCloseClick}>
      {enableIntMetadataTagging && enableIntMetadataTagging === true && (
        <>
          <Tooltip
            PopperProps={{
              disablePortal: false,
            }}
            //onClose={() => setOpenTooltipIntIngestStatus(false)}
            open={openTooltipIntIngestStatus}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={_renderTooltip("ingestStatus")}
            placement="bottom"
            arrow
          >
            <Button variant="whiteSoft" onClick={() => setOpenTooltipIntIngestStatus((prev) => !prev)}>
              {t("button:setIngestStatus")}
            </Button>
          </Tooltip>
          <Tooltip
            PopperProps={{
              disablePortal: false,
            }}
            //onClose={() => setOpenTooltipIntBillingStatus(false)}
            open={openTooltipIntBillingStatus}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={_renderTooltip("billingStatus")}
            placement="bottom"
            arrow
          >
            <Button variant="whiteSoft" onClick={() => setOpenTooltipIntBillingStatus((prev) => !prev)}>
              {t("button:setBillingStatus")}
            </Button>
          </Tooltip>
          <Tooltip
            PopperProps={{
              disablePortal: false,
            }}
            //onClose={() => setOpenTooltipIntWoIdStatus(false)}
            open={openTooltipIntWoIdStatus}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={_renderTooltip("btId")}
            placement="bottom"
            arrow
          >
            <Button variant="whiteSoft" onClick={() => setOpenTooltipIntWoIdStatus((prev) => !prev)}>
              {t("button:setWorkOrderId")}
            </Button>
          </Tooltip>
        </>
      )}

      <Divider
        orientation="vertical"
        flexItem
        sx={(theme) => ({ borderColor: theme.palette.backgroundBoldAlpha.border, m: 0.5 })}
      />

      <Button
        variant="white"
        startIcon={<PeopleIcon />}
        onClick={() => handleSetFollowers()}
        disabled={!UserHasAcl(AclTypes.aclOrderAssignFollowers)}
      >
        {t("common:setFollowers")}
      </Button>

      {enableDelete && enableDelete === true && (
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => handleDelete()}
          disabled={!UserHasAcls([AclTypes.aclIngestDeleteRequest, AclTypes.aclElevationWishlistCreate])}
        >
          {t("button:deleteRequests", { count: items.length })}
        </Button>
      )}
    </SelectModeBanner>
  );
};

export default IngestBanner;
